import React from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import { DEFAULT_QUERY_OPTIONS } from '../../../../support/constants';
import {
  apiDelete,
  apiGet,
  apiPostJson,
  apiPutJson,
} from '../../../../support/aiApi';

export type TuningFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'hidden'
  | 'json';

export type TuningField = {
  name: string;
  label: string;
  type?: TuningFieldType;
  required?: boolean;
  helperText?: string;
  options?: Array<{ label: string; value: any }>;
  minRows?: number;
  width?: number;
  readonly?: boolean;
};

export type TuningColumn = {
  key: string;
  label: string;
  width?: number;
  render?: (row: any) => React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
};

type Props = {
  title: string;
  description?: string;
  endpoint: string;
  queryKey: readonly unknown[];
  fields: TuningField[];
  defaultValues?: Record<string, any>;
  extraParams?: Record<string, any>;
  orderBy?: string;
  idField?: string;
  rowTitle?: (row: any) => string;
  rowSubtitle?: (row: any) => string;
  columns?: TuningColumn[];
  selectedId?: string | number | null;
  onRowClick?: (row: any) => void;
  addLabel?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  allowEdit?: boolean;
  allowDelete?: boolean;
  renderRowActions?: (row: any, helpers: { refetch: () => void }) => React.ReactNode;
  defaultPageSize?: number;
  searchParam?: string;
  deleteConfirmTitle?: string;
  deleteConfirmMessage?: (row: any) => string;
  onDeleteSuccess?: (row: any) => void;
};

export function normalizeItems(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.records)) return data.records;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

export function getTotalCount(data: any, fallback: number) {
  if (!data || Array.isArray(data)) return fallback;

  return (
    data.total_count ??
    data.total ??
    data.count ??
    data.meta?.total ??
    data.pagination?.total ??
    fallback
  );
}

export function boolChip(value: any) {
  return (
    <Chip
      size="small"
      color={value ? 'success' : 'default'}
      variant={value ? 'filled' : 'outlined'}
      label={String(Boolean(value))}
    />
  );
}

export function safePreview(value: any, max = 160) {
  if (value === null || value === undefined || value === '') return '-';

  const text =
    typeof value === 'string'
      ? value
      : (() => {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      })();

  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function normalizeOrderBy(orderBy?: string) {
  if (!orderBy) return undefined;
  const trimmed = String(orderBy).trim();
  if (!trimmed) return undefined;
  return trimmed.split(',')[0].trim() || undefined;
}

function initialSort(orderBy?: string) {
  const normalized = normalizeOrderBy(orderBy);

  if (!normalized) {
    return {
      field: '',
      direction: 'asc' as 'asc' | 'desc',
    };
  }

  if (normalized.startsWith('-')) {
    return {
      field: normalized.slice(1),
      direction: 'desc' as 'asc' | 'desc',
    };
  }

  return {
    field: normalized,
    direction: 'asc' as 'asc' | 'desc',
  };
}

function buildOrderBy(field: string, direction: 'asc' | 'desc') {
  if (!field) return undefined;
  return direction === 'desc' ? `-${field}` : field;
}

function safeJsonStringify(value: any) {
  if (value === undefined || value === null || value === '') return '{}';

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
}

function coercePayloadValue(field: TuningField, value: any) {
  if (field.type === 'number') {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }

  if (field.type === 'boolean') {
    return Boolean(value);
  }

  if (field.type === 'json') {
    if (value === '' || value === null || value === undefined) return {};
    if (typeof value !== 'string') return value;
    return JSON.parse(value);
  }

  return value;
}

const ConfirmDeleteDialog: React.FC<{
  open: boolean;
  title: string;
  message: string;
  deleting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, title, message, deleting, onCancel, onConfirm }) => {
  return (
    <Dialog open={open} onClose={deleting ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This action cannot be undone.
        </Alert>

        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>

        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RecordDialog: React.FC<{
  open: boolean;
  title: string;
  fields: TuningField[];
  initialValue: Record<string, any>;
  onClose: () => void;
  onSubmit: (payload: Record<string, any>) => Promise<void>;
  submitting?: boolean;
}> = ({
        open,
        title,
        fields,
        initialValue,
        onClose,
        onSubmit,
        submitting,
      }) => {
  const [form, setForm] = React.useState<Record<string, any>>({});
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const next: Record<string, any> = { ...(initialValue || {}) };

    for (const field of fields) {
      if (field.type === 'json') {
        next[field.name] = safeJsonStringify(next[field.name]);
      }
    }

    setForm(next);
    setError(null);
  }, [open, initialValue, fields]);

  const setField = (name: string, value: any) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError(null);

    try {
      const payload = { ...form };

      for (const field of fields) {
        if (field.required) {
          const value = payload[field.name];
          if (value === undefined || value === null || String(value).trim() === '') {
            setError(`${field.label} is required.`);
            return;
          }
        }

        payload[field.name] = coercePayloadValue(field, payload[field.name]);
      }

      await onSubmit(payload);
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Grid container spacing={2}>
            {fields
              .filter((field) => field.type !== 'hidden')
              .map((field) => {
                const type = field.type || 'text';
                const value = form[field.name];

                if (type === 'boolean') {
                  return (
                    <Grid item xs={12} md={field.width || 4} key={field.name}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(value)}
                            disabled={field.readonly}
                            onChange={(event) => setField(field.name, event.target.checked)}
                          />
                        }
                        label={field.label}
                      />
                      {field.helperText ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {field.helperText}
                        </Typography>
                      ) : null}
                    </Grid>
                  );
                }

                if (type === 'select') {
                  return (
                    <Grid item xs={12} md={field.width || 4} key={field.name}>
                      <TextField
                        fullWidth
                        select
                        label={field.label}
                        value={value ?? ''}
                        disabled={field.readonly}
                        required={field.required}
                        helperText={field.helperText}
                        onChange={(event) => setField(field.name, event.target.value)}
                      >
                        {(field.options || []).map((option) => (
                          <MenuItem key={String(option.value)} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  );
                }

                return (
                  <Grid
                    item
                    xs={12}
                    md={type === 'textarea' || type === 'json' ? 12 : field.width || 6}
                    key={field.name}
                  >
                    <TextField
                      fullWidth
                      label={field.label}
                      value={value ?? ''}
                      disabled={field.readonly}
                      required={field.required}
                      helperText={field.helperText}
                      type={type === 'number' ? 'number' : 'text'}
                      multiline={type === 'textarea' || type === 'json'}
                      minRows={type === 'json' ? 8 : field.minRows || 4}
                      onChange={(event) => setField(field.name, event.target.value)}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TuningAdminTable: React.FC<Props> = ({
                                             title,
                                             description,
                                             endpoint,
                                             queryKey,
                                             fields,
                                             defaultValues = {},
                                             extraParams = {},
                                             orderBy,
                                             idField = 'id',
                                             rowTitle,
                                             rowSubtitle,
                                             columns,
                                             selectedId,
                                             onRowClick,
                                             addLabel = 'Add',
                                             disabled,
                                             allowCreate = true,
                                             allowEdit = true,
                                             allowDelete = true,
                                             renderRowActions,
                                             defaultPageSize = 25,
                                             searchParam = 'search',
                                             deleteConfirmTitle,
                                             deleteConfirmMessage,
                                             onDeleteSuccess,
                                           }) => {
  const queryClient = useQueryClient();

  const initial = React.useMemo(() => initialSort(orderBy), [orderBy]);

  const [filter, setFilter] = React.useState('');
  const [debouncedFilter, setDebouncedFilter] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [sortField, setSortField] = React.useState(initial.field);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(initial.direction);
  const [editing, setEditing] = React.useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = React.useState<any | null>(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFilter(filter.trim());
      setPage(0);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [filter]);

  const extraParamsKey = JSON.stringify(extraParams);
  React.useEffect(() => {
    setPage(0);
  }, [extraParamsKey]);

  const orderByParam = buildOrderBy(sortField, sortDirection);

  const fullQueryKey = React.useMemo(
    () => [
      ...queryKey,
      {
        page,
        pageSize,
        sortField,
        sortDirection,
        filter: debouncedFilter,
        extraParams,
      },
    ],
    [queryKey, page, pageSize, sortField, sortDirection, debouncedFilter, extraParams]
  );

  const query = useQuery<any>({
    queryKey: fullQueryKey,
    queryFn: () =>
      apiGet<any>(endpoint, {
        page: page + 1,
        size: pageSize,
        detailed: true,
        ...(orderByParam ? { 'order-by': orderByParam } : {}),
        ...(debouncedFilter ? { [searchParam]: debouncedFilter } : {}),
        ...extraParams,
      }),
    ...DEFAULT_QUERY_OPTIONS,
    placeholderData: keepPreviousData,
    enabled: !disabled,
  });

  const rows = normalizeItems(query.data);
  const totalCount = getTotalCount(query.data, rows.length);

  const saveMutation = useMutation<any, Error, any>({
    mutationFn: async (payload) => {
      const id = payload?.[idField];

      if (id) {
        return apiPutJson<any>(`${endpoint}${id}/`, payload);
      }

      return apiPostJson<any>(endpoint, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      setDialogOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation<any, Error, any>({
    mutationFn: async (row) => {
      const id = row?.[idField];
      if (!id) throw new Error('Record id is required.');
      return apiDelete<any>(`${endpoint}${id}/`);
    },
    onSuccess: async (_data, row) => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      setPendingDeleteRow(null);
      onDeleteSuccess?.(row);
    },
  });

  const openCreate = () => {
    setEditing({ ...defaultValues });
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const requestDelete = (row: any) => {
    setPendingDeleteRow(row);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteRow) return;
    await deleteMutation.mutateAsync(pendingDeleteRow);
  };

  const handleSort = (column: TuningColumn) => {
    if (column.sortable === false) return;

    const nextField = column.sortKey || column.key;
    if (!nextField) return;

    if (sortField === nextField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(nextField);
      setSortDirection('asc');
    }

    setPage(0);
  };

  const tableColumns: TuningColumn[] =
    columns ||
    [
      {
        key: 'name',
        label: 'Name',
        render: (row: any) => (
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {rowTitle?.(row) || row.name || row.key || row.entity_type || row.id}
            </Typography>
            {rowSubtitle ? (
              <Typography variant="caption" color="text.secondary">
                {rowSubtitle(row)}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        key: 'is_active',
        label: 'Active',
        width: 100,
        render: (row: any) => boolChip(row.is_active),
      },
      {
        key: 'updated_at',
        label: 'Updated',
        width: 180,
        render: (row: any) => (row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'),
      },
    ];

  const pendingDeleteLabel =
    pendingDeleteRow
      ? rowTitle?.(pendingDeleteRow) ||
      pendingDeleteRow?.name ||
      pendingDeleteRow?.key ||
      pendingDeleteRow?.entity_type ||
      pendingDeleteRow?.id
      : '';

  const defaultDeleteMessage = pendingDeleteRow
    ? `You are about to delete:\n\n${pendingDeleteLabel}\n\nID: ${pendingDeleteRow?.[idField] || '-'}`
    : '';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="h6">{title}</Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => query.refetch()}>
            Refresh
          </Button>

          {allowCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={disabled}>
              {addLabel}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {query.isLoading || query.isFetching ? <LinearProgress sx={{ mb: 2 }} /> : null}

      {query.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(query.error as any)?.message || 'Failed to load records.'}
        </Alert>
      ) : null}

      <TextField
        fullWidth
        size="small"
        placeholder="Search/filter records..."
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {tableColumns.map((col) => {
                const colSortKey = col.sortKey || col.key;
                const active = sortField === colSortKey;

                return (
                  <TableCell key={col.key} sx={{ width: col.width }}>
                    {col.sortable === false ? (
                      col.label
                    ) : (
                      <TableSortLabel
                        active={active}
                        direction={active ? sortDirection : 'asc'}
                        onClick={() => handleSort(col)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                );
              })}

              {allowEdit || allowDelete || renderRowActions ? (
                <TableCell width={190} align="right">
                  Actions
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const id = row?.[idField];
              const selected = selectedId !== undefined && String(selectedId) === String(id);

              return (
                <TableRow
                  key={String(id)}
                  hover
                  selected={selected}
                  onClick={() => onRowClick?.(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {tableColumns.map((col) => (
                    <TableCell key={col.key}>
                      {col.render ? col.render(row) : safePreview(row?.[col.key])}
                    </TableCell>
                  ))}

                  {allowEdit || allowDelete || renderRowActions ? (
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      {renderRowActions ? renderRowActions(row, { refetch: query.refetch }) : null}

                      {allowEdit ? (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}

                      {allowDelete ? (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => requestDelete(row)}
                            disabled={deleteMutation.isPending}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {!rows.length && !query.isLoading ? (
        <Typography variant="body2" color="text.secondary" mt={2}>
          No records found.
        </Typography>
      ) : null}

      <TablePagination
        component="div"
        count={Number(totalCount || 0)}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      <RecordDialog
        open={dialogOpen}
        title={editing?.[idField] ? `Edit ${title}` : `Add ${title}`}
        fields={fields}
        initialValue={editing || defaultValues}
        submitting={saveMutation.isPending}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSubmit={async (payload) => {
          await saveMutation.mutateAsync(payload);
        }}
      />

      <ConfirmDeleteDialog
        open={!!pendingDeleteRow}
        title={deleteConfirmTitle || `Delete ${title}`}
        message={
          pendingDeleteRow && deleteConfirmMessage
            ? deleteConfirmMessage(pendingDeleteRow)
            : defaultDeleteMessage
        }
        deleting={deleteMutation.isPending}
        onCancel={() => setPendingDeleteRow(null)}
        onConfirm={confirmDelete}
      />
    </Paper>
  );
};

export default TuningAdminTable;